import React, { Dispatch, SetStateAction, useState } from "react";
import { Avatar, Input, Upload, Typography, Flex } from "antd";
import { toast } from "react-toastify";
import { CameraOutlined, CloseOutlined } from "@ant-design/icons";
import { useAppDispatch } from "../../../redux/utils/Dispatcher";
import { formatSingleChat } from "../../../utils";
import { useChatSidebarContext } from "../../../context/ChatSidebarScreenContext";
import ChatSidebarHeader from "../../../components/common/ChatSidebarHeader";
import GradientButton from "../../../components/common/UI/GradientButton";
import ENDPOINTS from "../../../services/endpoint";
import apiInstance from "../../../services/apiService";
import "./index.scss";
import { setCurrentChat } from "../../../redux/slices/chatSlice";

const { Text } = Typography;

interface Contact {
  _id: string;
  fullName: string;
  phoneNumber: string;
  emailAddress: string;
  iseFone: boolean;
}

interface CreateGroupProps {
  selectedMembers: Contact[] | null | [];
  handleBackClick: () => void;
  setSelectedMembers: Dispatch<SetStateAction<any>>;
}

const CreateGroup: React.FC<CreateGroupProps> = ({
  selectedMembers,
  handleBackClick,
  setSelectedMembers,
}) => {
  const dispatch = useAppDispatch();
  const { setCurrentScreen } = useChatSidebarContext();
  const [groupName, setGroupName] = useState<string>("");
  const [groupImage, setGroupImage] = useState<File | null>(null);
  const [groupImageURL, setGroupImageURL] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleImageUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setGroupImageURL(e.target.result as string);
        setGroupImage(file);
      }
    };
    reader.readAsDataURL(file);
    return false;
  };

  const handleRemoveMember = (member: Contact) => {
    if (loading) return;
    setSelectedMembers((prev: any) => {
      return prev.filter((c: Contact) => c.phoneNumber !== member.phoneNumber);
    });
  };

  const handleCreateGroup = async () => {
    if (!groupName || !groupName.trim()) {
      toast.error("Group name is required");
      return;
    }

    if (!selectedMembers?.length) {
      toast.error("Please select at least 1 group member");
      return;
    }

    const URL = ENDPOINTS.CREATE_GROUP;
    setLoading(true);

    try {
      const formData = new FormData();

      formData.append("name", groupName);

      selectedMembers.forEach((member: Contact, index: number) => {
        formData.append(`groupMembers[${index}]`, member.phoneNumber);
      });

      if (groupImage) {
        formData.append("file", groupImage);
      }

      const response = await apiInstance.post(URL, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200) {
        setCurrentScreen("chats");
        dispatch(setCurrentChat(formatSingleChat(response.data.conversation)));
      }
    } catch (err: any) {
      toast.error(err.message || "Error creating group. Please try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-group-screen">
      <ChatSidebarHeader title="New Group" handleBackClick={handleBackClick} />

      <div className="name-container">
        <Upload
          className="image-upload"
          beforeUpload={(file) => handleImageUpload(file)}
          multiple={false}
          showUploadList={false}
          disabled={loading}
          accept="image/*"
        >
          {groupImageURL ? (
            <img src={groupImageURL} alt="Profile" />
          ) : (
            <CameraOutlined className="camera" />
          )}
        </Upload>

        <Input
          value={groupName}
          disabled={loading}
          onChange={(e) => setGroupName(e.target.value)}
          className="custom-input group-name-input"
          placeholder="Group name"
        />
      </div>

      <div className="banner">{selectedMembers?.length} Members</div>

      <div className="selected-members-container">
        <Flex align="center" gap={8} wrap>
          {selectedMembers?.map((member: Contact) => (
            <div key={`${member._id}-${member.phoneNumber}`} className="member">
              <CloseOutlined
                onClick={() => handleRemoveMember(member)}
                className="cancel-icon"
              />
              <Avatar className="member-avatar" size={50}>
                {member?.fullName.slice(0, 1)}
              </Avatar>
              <Text className="member-name">{member?.fullName}</Text>
              <Text className="member-number">{member?.phoneNumber}</Text>
            </div>
          ))}
        </Flex>
        <GradientButton
          loading={loading}
          onClick={handleCreateGroup}
          className="create-btn"
        >
          Create
        </GradientButton>
      </div>
    </div>
  );
};

export default CreateGroup;
